import { useState, useCallback, useEffect } from 'react';
import { useApi, ApiConfig } from './useApi';
import { Duck, PaginatedResponse } from './types';

export interface UseDucksOptions {
  autoFetch?: boolean;
  page?: number;
  pageSize?: number;
  ordering?: string;
}

export function useDucks(
  config: ApiConfig = {},
  options: UseDucksOptions = {},
) {
  const { autoFetch = true, page = 1, pageSize = 20, ordering } = options;
  const api = useApi(config);
  const [ducks, setDucks] = useState<Duck[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState<string | null>(null);
  const [previousPage, setPreviousPage] = useState<string | null>(null);

  const fetchDucks = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
      });
      
      if (ordering) {
        params.append('ordering', ordering);
      }

      const response = await api.get<PaginatedResponse<Duck>>(
        `/ducks/?${params}`,
      );
      setDucks(response.results);
      setTotalCount(response.count);
      setNextPage(response.next);
      setPreviousPage(response.previous);
      return response;
    } catch (error) {
      console.error('Failed to fetch ducks:', error);
      throw error;
    }
  }, [api, page, pageSize, ordering]);

  const getDuck = useCallback(
    async (id: number) => {
      return api.get<Duck>(`/ducks/${id}/`);
    },
    [api],
  );

  const createDuck = useCallback(
    async (data: Omit<Duck, 'id'>) => {
      const response = await api.post<Duck>('/ducks/', data);
      await fetchDucks();
      return response;
    },
    [api, fetchDucks],
  );

  const updateDuck = useCallback(
    async (id: number, data: Partial<Omit<Duck, 'id'>>) => {
      const response = await api.patch<Duck>(`/ducks/${id}/`, data);
      await fetchDucks();
      return response;
    },
    [api, fetchDucks],
  );

  const deleteDuck = useCallback(
    async (id: number) => {
      await api.delete(`/ducks/${id}/`);
      await fetchDucks();
    },
    [api, fetchDucks],
  );

  const addTag = useCallback(
    async (duckId: number, tagName: string) => {
      const duck = await getDuck(duckId);
      const updatedTags = [...duck.tags, tagName];
      return updateDuck(duckId, { tag_names: updatedTags });
    },
    [getDuck, updateDuck],
  );

  const removeTag = useCallback(
    async (duckId: number, tagName: string) => {
      const duck = await getDuck(duckId);
      const updatedTags = duck.tags.filter((tag) => tag !== tagName);
      return updateDuck(duckId, { tag_names: updatedTags });
    },
    [getDuck, updateDuck],
  );

  useEffect(() => {
    if (autoFetch && config.token) {
      fetchDucks();
    }
  }, [autoFetch, config.token, fetchDucks]);

  return {
    ducks,
    totalCount,
    nextPage,
    previousPage,
    loading: api.loading,
    error: api.error,
    fetchDucks,
    getDuck,
    createDuck,
    updateDuck,
    deleteDuck,
    addTag,
    removeTag,
  };
}
